<template>
    <div class="row">
        <div class="col-12 mb-3 d-flex flex-row align-items-center justify-content-between">
            <h1>{{  $t('view-activity') }}</h1>

            <button class="btn btn-primary" @click="$router.push(`/reflections/save/${activity.id}`)">
                <b-icon icon="pencil" class="mr-2"/>{{ $t('edit') }}
            </button>
        </div>
        <div class="col-12 col-md-7 mb-3" v-if="!loading && activity.id">

             <!-- Reflection -->
            <div class="list-group border-radius-2 no-border mb-3">
                <div class="list-group-item">
                    <h3 class="mb-0">{{ $datetime.getLocaleStringFromTimeStamp(activity.datetime) }}</h3>
                </div>
                <div class="list-group-item" style="white-space: pre-wrap;">
                    {{ activity.reflection }}
                </div>
            </div>

            <!-- Assets -->
            <div v-if="activity.assets && activity.assets.length" class="list-group border-radius-2 no-border mb-3">
                <div class="list-group-item text-sm font-weight-bold">{{ $t('files') }}</div>
                <div class="list-group-item d-flex flex-row align-items-center flex-wrap">
                	<template v-for="asset in activity.assets">
						<img
							v-if="isImage(asset)"
							:key="asset.id"
							:src="getAssetURL(asset)"
							class="img-fluid"
							:alt="asset.asset_title"
							style="cursor: pointer;"
							@click.stop="goToAssetViewUrl(asset)"
						>
						<AssetBadge
							v-else
							:file="asset"
							:key="asset.id"
							class="mr-2"
							@click="goToAssetViewUrl(asset)"
							style="cursor: pointer;"
						/>
                    </template>
                </div>
            </div>

            <!-- Treatment Sessions -->
            <div v-if="activity.treatmentsessions && activity.treatmentsessions.length" class="list-group border-radius-2 no-border mb-3">
                <div class="list-group-item text-sm font-weight-bold">
                    {{ $t('treatment-session') }}
                </div>
                <TreatmentSessionListItem
                    v-for="session in activity.treatmentsessions"
                    :key="session.id"
                    :session="session"
                    @click="$router.push(`/clinical/treatmentsession/${session.id}`)"
                    style="cursor: pointer;"
                />
            </div>

            <!-- Competency Test Sessions -->
            <div v-if="activity.competencytestsessions && activity.competencytestsessions.length" class="list-group border-radius-2 no-border mb-3">
                <div class="list-group-item text-sm font-weight-bold">
                    {{ $t('competency-test') }}
                </div>
                <TestSessionListItem
                    v-for="session in activity.competencytestsessions"
                    :key="session.id"
                    :session="session"
                    @click="$router.push(`/tests/${session.id}`)"
                    class="list-group-item"
                    style="cursor: pointer;"
                />
            </div>

            <!-- Form Responses -->
            <div v-if="activity.formresponses && activity.formresponses.length" class="list-group border-radius-2 no-border mb-3">
                <div class="list-group-item text-sm font-weight-bold">
                    {{ $t('forms') }}
                </div>
                <FormResponseListItem
                    v-for="response in activity.formresponses"
                    :key="response.formresponse_id"
                    :formResponse="response"
                    @click="$router.push(`/forms/view/${response.formresponse_id}`)"
                    class="list-group-item"
                    style="cursor: pointer;"
                />
            </div>

            <!-- Learning Outcomes -->
            <div v-if="activity.learningoutcomes && activity.learningoutcomes.length" class="list-group border-radius-2 no-border mb-3">
                <div class="list-group-item text-sm font-weight-bold">
                    {{  $t('learning-outcomes') }}
                </div>
                <div
                    v-for="outcome in activity.learningoutcomes"
                    :key="outcome.id"
                    class="list-group-item"
                    @click="$router.push(`/curriculum-mapping/learning-outcomes/edit/${outcome.id}`)"
                    style="cursor: pointer;"
                >
                    {{ outcome.title }}
                </div>
            </div>
        </div>

        <div class="col-12 col-md-5 mb-3">
            <TagGroups :value="activity.tags" :view_only="true" />
        </div>
    </div>
</template>

<script>
import TagGroups from '../../components/Tags/GroupedTagSelector.vue'
import TreatmentSessionListItem from '../../components/TreatmentSessions/SessionListItem.vue'
import TestSessionListItem from '../../components/CompetencyTests/SessionListItem.vue'
import FormResponseListItem from '../../components/FormResponses/ListItem.vue'
import AssetBadge from '../../components/Asset/AssetBadge.vue'

export default {
    components: {
        TagGroups,
        TreatmentSessionListItem,
        TestSessionListItem,
        FormResponseListItem,
        AssetBadge,
    },
    mounted() {
        this.fetchActivity().then(() => { this.loading = false })
    },
    data() {
        return {
            loading: true,
            activity: {
                id: 0,
            }
        }
    },
    methods: {
        fetchActivity() {
            this.$store.commit('showBlockingSpinner')

            return this.$cfetch(`${this.$settings.APIBaseURL}/activitylog/get`, {
                method: 'GET',
                data: {
                    id: this.$route.params.activityId
                }
            }).then((res) => {
                if (res.status !== 'SUCCESS') throw new Error('Unexpected error fetching activity')

                this.activity = res.result
            }).catch((err) => {
                console.error(err)
                this.$log.error.call(this, err)

                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-fetching-activity'),
                    confirmCallBack: function () { this.$router.push('/reflections') }
                })
            }).finally(() => {
                this.$store.commit('hideBlockingSpinner')
            })
        },
    	isImage: function(item) {
    		return (
    			item.asset_extension == 'jpg' ||
				item.asset_extension == 'jpeg' ||
				item.asset_extension == 'png' ||
				item.asset_extension == 'gif'
			);
    	},
    	getAssetURL: function(item) {
    		return `${this.$settings.APIBaseURL}/asset/fetch/${item.asset_id}.${
				item.asset_extension
			}?username=${this.$UserSession.getValue.call(
				this,
				"username"
			)}&authToken=${this.$UserSession.getValue.call(this, "authToken")}`;
    	},
        goToAssetViewUrl(item) {
            window.location.assign(this.getAssetURL(item));
		},
    },
}
</script>

<style lang="scss">
a, a:hover {
    text-decoration: none !important;
}
</style>
